import type { MindGraph, GraphNode } from './types';

/**
 * Kahn topological order; nodes in any leftover cycle are appended last so they
 * still evaluate (with partial input) rather than hang. Shared by the evaluator.
 */
export function topoOrder(graph: MindGraph): GraphNode[] {
  const byId = new Map(graph.nodes.map((n) => [n.id, n]));
  const indeg = new Map<string, number>();
  for (const n of graph.nodes) indeg.set(n.id, 0);
  for (const e of graph.edges) {
    if (byId.has(e.toNode) && byId.has(e.fromNode)) {
      indeg.set(e.toNode, (indeg.get(e.toNode) ?? 0) + 1);
    }
  }
  const queue: string[] = [];
  for (const [id, d] of indeg) if (d === 0) queue.push(id);
  const order: GraphNode[] = [];
  const seen = new Set<string>();
  while (queue.length) {
    const id = queue.shift() as string;
    if (seen.has(id)) continue;
    seen.add(id);
    const node = byId.get(id);
    if (node) order.push(node);
    for (const e of graph.edges) {
      if (e.fromNode !== id) continue;
      const d = (indeg.get(e.toNode) ?? 0) - 1;
      indeg.set(e.toNode, d);
      if (d <= 0 && !seen.has(e.toNode)) queue.push(e.toNode);
    }
  }
  for (const n of graph.nodes) if (!seen.has(n.id)) order.push(n);
  return order;
}
